/** @jsx jsx */
/** @jsxFrag null */
// @ts-strict-ignore
// eslint-disable-next-line no-restricted-imports
import {jsx} from 'panel';

import {SvgIconEl} from 'common/components/svg-icon';
import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {InputEl} from 'common/components/input';
import {SelectEl} from 'common/components/select';
import {InputLabelEl} from 'common/components/input/label';
import {SpinnerEl} from 'common/components/spinner';
import {KAPA_DESCRIPTION_ID, KAPA_SUBJECT_ID} from 'common/util/kapa';

import {type SupportWidgetForm} from './index';
import {Severity, SeverityLabels} from './types';

export function template(this: SupportWidgetForm) {
  const t = this.getContext(`i18n`).t;
  return (
    <div
      sel='.support-widget-container'
      class={{
        maximized: this.state.isMaximized,
        minimized: this.state.isMinimized,
      }}
    >
      <div sel='.support-widget'>
        <header
          on={{
            click: (ev) => (this.state.isMinimized ? this.helpers.toggleMinimize(ev, false) : null),
          }}
        >
          <div>
            {this.state.isTicketSubmitted ? t(`help.support.submitted-title`) : t(`help.support.pending-title`)}
          </div>
          {!(this.state.isMaximized || this.state.isMinimized) ? (
            <SvgIconEl attrs={{icon: `dash`}} on={{click: (ev) => this.helpers.toggleMinimize(ev, true)}} />
          ) : null}
          <SvgIconEl
            attrs={{
              elref: `toggleMaximizeButton`,
              icon: this.state.isMaximized ? `collapse` : `expand`,
            }}
            on={{
              click: this.helpers.toggleFullscreen,
            }}
          />
        </header>
        {this.state.isTicketSubmitted ? (
          <>
            <div sel='.zendesk-ticket-submitted'>
              <SvgIconEl attrs={{icon: `circle-checkmark`}} />
              <div sel='.thanks'>{t(`help.support.thank-you`)}</div>
              <div sel='.confirmation'>
                {t(`help.support.thank-you-followup`, {userEmail: this.attr(`user-email`)})}
              </div>
            </div>
            <footer sel='.buttons'>
              <ButtonEl
                sel='.done'
                attrs={{
                  theme: ButtonTheme.Primary,
                }}
                on={{
                  click: this.helpers.closeSupportWidget,
                }}
              >
                {t(`help.support.done`)}
              </ButtonEl>
            </footer>
          </>
        ) : (
          <>
            <div sel='.new-zendesk-ticket'>
              {this.helpers.showSeverityDropdown() ? (
                <div sel='.form-field'>
                  <InputLabelEl attrs={{label: t(`help.support.priority`)}} />
                  <SelectEl
                    attrs={{
                      elref: `severityField`,
                      'selected-option': SeverityLabels[this.state.severity],
                      size: ButtonSize.Large,
                    }}
                    props={{
                      sections: [
                        {
                          items: [
                            {label: SeverityLabels[Severity.SEV_0], value: Severity.SEV_0},
                            {label: SeverityLabels[Severity.SEV_1], value: Severity.SEV_1},
                            {label: SeverityLabels[Severity.SEV_2], value: Severity.SEV_2},
                            {label: SeverityLabels[Severity.DEFAULT], value: Severity.DEFAULT},
                          ],
                        },
                      ],
                    }}
                    on={{change: (ev) => this.helpers.selectSeverity(ev)}}
                  />
                </div>
              ) : null}
              <div sel='.form-field'>
                <InputLabelEl attrs={{label: t(`help.support.request-type`)}} />
                <SelectEl
                  attrs={{
                    elref: `requestType`,
                    'selected-option': t(this.state.requestTypeLabel) || t(`help.support.request-type-prompt`),
                    size: ButtonSize.Large,
                  }}
                  props={{
                    sections: [{items: this.helpers.getRequestTypeOptions()}],
                  }}
                  on={{
                    change: (ev) => this.helpers.selectRequestType(ev),
                  }}
                />
              </div>
              <>
                <div sel='.remaining-form-fields' key='form-fields'>
                  {this.state.requestTypeLabel ? (
                    <div sel='.form-field'>
                      <InputLabelEl attrs={{label: t(this.helpers.getRequestSubtypeTicketField())}} />
                      <SelectEl
                        attrs={{
                          elref: `requestSubType`,
                          label: t(this.state.requestSubtypeLabel),
                          'disable-search': true,
                          'selected-option':
                            this.state.requestSubtypeLabel ||
                            t(`help.support.select-sub-type`, {
                              subType: t(this.helpers.getRequestSubtypeTicketField()),
                            }),
                          size: ButtonSize.Large,
                        }}
                        props={{
                          sections: [{items: this.helpers.getRequestSubtypeOptions()}],
                        }}
                        on={{
                          change: (ev) => this.helpers.selectRequestSubtype(ev),
                        }}
                      />
                    </div>
                  ) : null}
                  <div sel='.form-field'>
                    <InputEl
                      attrs={{
                        label: t(`help.support.subject`),
                        elref: `subjectField`,
                        'field-id': KAPA_SUBJECT_ID,
                      }}
                      on={{input: this.helpers.inputSubject}}
                      props={{value: this.state.subject || ``}}
                    />
                  </div>
                  <div sel='.form-field'>
                    <InputEl
                      attrs={{
                        type: `textarea`,
                        label: t(`help.support.issue-prompt`),
                        elref: `descriptionField`,
                        'field-id': KAPA_DESCRIPTION_ID,
                      }}
                      on={{
                        input: this.helpers.handleInputComment,
                        paste: this.helpers.handlePasteFromClipboard,
                      }}
                      props={{value: this.state.comment || ``}}
                    />
                  </div>
                  <div sel='.form-field'>
                    <InputLabelEl
                      attrs={{
                        label:
                          this.state.uploads.length === 0
                            ? t(`help.support.attachments`)
                            : `${t(`help.support.attachments`)} (${this.state.uploads.length})`,
                        necessary: `optional`,
                      }}
                    />
                    {this.state.isUploadingFiles ? (
                      <div sel='.uploading-file'>
                        <SpinnerEl>{t(`help.support.uploading`)}</SpinnerEl>
                      </div>
                    ) : null}
                    <label
                      sel='.upload-files'
                      attrs={{
                        for: `upload-files`,
                      }}
                      class={{
                        active: this.state.isDraggingAndDropping,
                        disabled: this.state.uploads.length === this.helpers.MAX_FILE_UPLOADS,
                      }}
                      on={{
                        dragleave: this.helpers.fileInputDragleave,
                        dragover: (ev) => this.helpers.fileInputDragover(ev),
                        drop: (ev) => this.helpers.uploadDroppedFiles(ev),
                      }}
                    >
                      <SvgIconEl attrs={{icon: `image`}} />
                      <div sel='.upload-files-text'>
                        {(() => {
                          const uploadsRemaining = this.helpers.MAX_FILE_UPLOADS - this.state.uploads.length;
                          const numUploads = this.state.uploads.length;
                          if (numUploads === 0) {
                            return t(`help.support.screenshots-none`, {maxUploads: this.helpers.MAX_FILE_UPLOADS});
                          } else if (numUploads < this.helpers.MAX_FILE_UPLOADS) {
                            return t(`help.support.screenshots-add`, {count: uploadsRemaining});
                          }
                          return t(`help.support.screenshots-limit-reached`);
                        })()}
                      </div>
                    </label>
                    <input
                      sel='#upload-files'
                      attrs={{
                        accept: `image/*`,
                        type: `file`,
                        multiple: true,
                      }}
                      hook={{
                        insert: (vnode) => this.helpers.setElRef(`fileInput`, vnode.elm),
                      }}
                      on={{
                        input: this.helpers.uploadSelectedFiles,
                      }}
                    />
                    <p>
                      {t(`help.support.need-help`)}
                      {` `}
                      <a attrs={{href: `https://docs.mixpanel.com/`}} on={{click: this.helpers.trackHelpDocs}}>
                        {t(`help.support.visit-docs`)}
                      </a>
                      {` `}
                      {t(`help.support.docs-tip`)}
                    </p>
                  </div>
                </div>
              </>
            </div>
            <footer sel='.buttons'>
              <ButtonEl
                sel='.cancel-button'
                attrs={{
                  elref: `cancelButton`,
                  theme: ButtonTheme.Tertiary,
                }}
                on={{
                  click: this.helpers.closeSupportWidget,
                }}
              >
                {t(`help.support.cancel`)}
              </ButtonEl>
              <ButtonEl
                sel='.submit-button'
                attrs={{
                  disabled: this.helpers.isSubmitDisabled(),
                  theme: ButtonTheme.Primary,
                }}
                on={{
                  click: this.helpers.submitZendeskTicket,
                }}
              >
                {this.state.isTicketSubmitting ? t(`help.support.sending`) : t(`help.support.send`)}
              </ButtonEl>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}
